import React from 'react';
import OETRow from './OETRow';
import { observer } from 'mobx-react';
import type { IOETRow } from '../../stores/model/uimodel/oetrow';
import type { IOETColumn } from '../../stores/model/uimodel/oetcolumn';

/**
 * Props for {@link OETBody}.
 *
 * - rows: the OET rows
 * - columns: the OET columns
 * - onSelect: callback to be called on select
 * - onRowHeaderClick: callback to be called when a row header is clicked
 * - onCellClick: callback to be called when a cell is clicked
 * - selectable: is selection enabled?
 * - selected: the currently selected element
 * - highlightedMethods: the currently highlighted methods
 * - popovers: are popovers enabled?
 * - draggedColumn: id of the column that is currently being dragged
 */
export interface OETBodyProps {
  rows: IOETRow[];
  columns: IOETColumn[];
  onSelect: (id: string) => void;
  onRowHeaderClick: (eventType: string) => void;
  onCellClick: (eventType: string, objectType: string) => void;
  selectable: boolean;
  selected: string | null;
  highlightedMethods: string[];
  popovers: boolean;
  draggedColumn: string | null;
}

const OETBody = ({
  rows,
  columns,
  onSelect,
  onRowHeaderClick,
  onCellClick,
  selectable,
  selected,
  highlightedMethods,
  popovers,
  draggedColumn,
}: OETBodyProps) => (
  <tbody>
    {rows
      // Skip rows whose event reference is dead (the modelReference resolver
      // returns `undefined` when the referenced event id is absent from the
      // metamodel — e.g. an orphan OET uimodel row left behind by an out-of-band
      // event deletion). Without this guard `r.event.id` throws and the
      // route-level error boundary takes down the whole project view.
      .filter((r) => r.event != null)
      .map((r) => (
        <OETRow
          key={r.event.id}
          columns={columns}
          row={r}
          selectable={selectable}
          onSelect={onSelect}
          onRowHeaderClick={onRowHeaderClick}
          onCellClick={onCellClick}
          selected={selected}
          highlightedMethods={highlightedMethods}
          popovers={popovers}
          draggedColumn={draggedColumn}
        />
      ))}
  </tbody>
);

const ObservedOETBody = observer(OETBody);
export default ObservedOETBody;
